import apiV2 from '../api/v2';
import { parseBlobExportError } from '../utils/blobError';

// Accounts-payable side of a charge: billing.purchase_order, its lines, the
// vendor invoice recorded against it, and its event history.

export type PurchaseOrderChannel = 'email' | 'external';
export type PurchaseOrderFulfillmentStatus = 'not_shipped' | 'shipped';
export type PurchaseOrderBillingStatus = 'not_billed' | 'billed';
// paid = has a payment date, unpaid = has none, independent of billingStatus:
// "billed and unpaid" needs billingStatus=billed AND paymentStatus=unpaid.
export type PurchaseOrderPaymentStatus = 'paid' | 'unpaid';

export interface PurchaseOrderVendorRef {
    id: string;
    name: string;
}

// No separate dispatch status to track (creation is the dispatch):
// `fulfillmentStatus` tracks the goods, `billingStatus` the money.
export interface PurchaseOrderListItem {
    id: string;
    poNumber: string;
    vendor: PurchaseOrderVendorRef;
    fulfillmentStatus: PurchaseOrderFulfillmentStatus;
    billingStatus: PurchaseOrderBillingStatus;
    originatingChargeId: string;
    practiceId: string;
    practiceName: string;
    practiceDoctorName: string | null;
    patientId: string | null;
    patientName: string | null;
    ordersVia: PurchaseOrderChannel;
    // Always set: a purchase order row only exists because it was dispatched.
    sentAt: string;
    // Line rollup: the EARLIEST shipment across the live lines.
    shippedAt: string | null;
    // Historical payment date only; billingStatus is the authority.
    paidAt: string | null;
    createdAt: string;
    lineCount: number;
    // Integer cents, summed over the lines that HAVE a vendor cost only -
    // `linesAwaitingCost` is how many were left out. See poCost.ts.
    expectedTotalCents: number;
    linesAwaitingCost: number;
    // Decimal DOLLAR string (NUMERIC(10,2)), not cents.
    vendorInvoicedAmount: string | null;
    vendorInvoiceNumber: string | null;
    // Serialized YYYY-MM-DD from a real DATE column, not a UTC instant.
    vendorInvoiceDueDate: string | null;
}

export interface PurchaseOrderLine {
    id: string;
    chargeLineItemId: string;
    productName: string;
    description: string;
    quantity: number;
    // null = this product has no vendor cost recorded (most of the catalog).
    expectedUnitCostCents: number | null;
    shippedQuantity: number;
    shippedAt: string | null;
    trackingNumber: string | null;
    canceledAt: string | null;
}

export interface PurchaseOrderEvent {
    id: string;
    entityType: string;
    eventType: string;
    actor: string;
    actorName: string | null;
    occurredAt: string;
    payload: unknown;
}

export interface PurchaseOrderFileRef {
    id: string;
    mimeType: string;
    originalFilename: string;
}

export interface PurchaseOrderDetail extends PurchaseOrderListItem {
    lines: PurchaseOrderLine[];
    vendorInvoiceFileId: string | null;
    // Read from the stored storage.file row - never sniffed from the URL.
    vendorInvoiceMimeType: string | null;
    vendorInvoiceFile: PurchaseOrderFileRef | null;
    documentFile: PurchaseOrderFileRef | null;
    documentFileId: string | null;
    sentTo: string[];
    events: PurchaseOrderEvent[];
}

export type PurchaseOrdersSortBy = 'createdAt' | 'vendorInvoiceDueDate';

// Workflow-stage axis the status cards toggle - orthogonal to the raw
// fulfillment/billing status filters, which stay available in More filters.
export type PurchaseOrderStage =
    'all' | 'awaitingShipment' | 'awaitingInvoice' | 'needsAttention' | 'complete';

export type PurchaseOrderStageSummary = Record<PurchaseOrderStage, { count: number }>;

export interface PurchaseOrdersListParams {
    page: number;
    limit: number;
    chargeId?: string;
    practiceId?: string;
    // Multi-select: the whole selection goes on the wire, not just the first.
    vendorId?: string[];
    fulfillmentStatus?: string[];
    billingStatus?: string[];
    needsAttention?: boolean;
    q?: string;
    sort?: PurchaseOrdersSortBy;
    order?: 'asc' | 'desc';
}

export interface PurchaseOrdersListResult {
    data: PurchaseOrderListItem[];
    total: number;
    stageSummary?: PurchaseOrderStageSummary;
}

export interface UpdatePurchaseOrderLineBody {
    shippedQuantity?: number;
    shippedAt?: string;
    trackingNumber?: string;
}

export interface RecordVendorInvoiceBody {
    vendorInvoiceNumber: string;
    // Dollars, matching the NUMERIC(10,2) column this lands in.
    vendorInvoicedAmount: number;
    vendorInvoiceDueDate?: string;
    vendorInvoiceFileId?: string;
    // Omitted leaves the stored value alone, null clears it, a date sets it.
    paidAt?: string | null;
}

// Records an order a human already placed on the vendor's site - no email is
// sent. orderMethodId must be an 'external' method belonging to vendorId.
export interface CreatePurchaseOrderBody {
    chargeLineItemIds: string[];
    vendorId: string;
    orderMethodId: string;
    poNumber?: string;
    sentAt?: string;
}

interface ListEnvelope {
    data: PurchaseOrderListItem[];
    total?: number;
    meta?: { total?: number; stageSummary?: PurchaseOrderStageSummary };
}

const BASE_PATH = '/billing/purchase-orders';

/** Backend-enforced ceiling on the list's `q` search param. */
export const PURCHASE_ORDERS_SEARCH_MAX_LENGTH = 120;

export const fetchPurchaseOrders = async (
    params: PurchaseOrdersListParams
): Promise<PurchaseOrdersListResult> => {
    const query: Record<string, string | string[]> = {
        page: String(params.page),
        limit: String(params.limit)
    };
    if (params.q) query.q = params.q;
    if (params.chargeId) query.chargeId = params.chargeId;
    if (params.practiceId) query.practiceId = params.practiceId;
    if (params.vendorId?.length) query.vendorId = params.vendorId;
    if (params.fulfillmentStatus?.length) query.fulfillmentStatus = params.fulfillmentStatus;
    if (params.billingStatus?.length) query.billingStatus = params.billingStatus;
    if (params.needsAttention) query.needsAttention = 'true';
    if (params.sort) query.sort = params.sort;
    if (params.order) query.order = params.order;

    // `meta.total` is the house standard; the top-level `total` is a legacy
    // duplicate being removed, kept only as a transitional fallback.
    const response = await apiV2.get<ListEnvelope>(BASE_PATH, { params: query });
    const body = response.data;
    return {
        data: body?.data ?? [],
        total: body?.meta?.total ?? body?.total ?? 0,
        stageSummary: body?.meta?.stageSummary
    };
};

export const createPurchaseOrder = async (
    body: CreatePurchaseOrderBody
): Promise<PurchaseOrderDetail> => {
    const response = await apiV2.post<{ success: boolean; data: PurchaseOrderDetail }>(
        BASE_PATH,
        body
    );
    return response.data.data;
};

// Retired vendors included: a retired vendor's live POs stay in the list, and a
// filter that omitted it would leave those rows unfilterable.
export const fetchBillingVendors = async (): Promise<PurchaseOrderVendorRef[]> => {
    const response = await apiV2.get<{ success: boolean; data: PurchaseOrderVendorRef[] }>(
        '/billing/vendors',
        { params: { includeInactive: 'true' } }
    );
    return response.data.data;
};

export const fetchPurchaseOrder = async (id: string): Promise<PurchaseOrderDetail> => {
    const response = await apiV2.get<{ success: boolean; data: PurchaseOrderDetail }>(
        `${BASE_PATH}/${id}`
    );
    return response.data.data;
};

export const updatePurchaseOrderLine = async (
    id: string,
    lineId: string,
    body: UpdatePurchaseOrderLineBody
): Promise<PurchaseOrderDetail> => {
    const response = await apiV2.patch<{ success: boolean; data: PurchaseOrderDetail }>(
        `${BASE_PATH}/${id}/lines/${lineId}`,
        body
    );
    return response.data.data;
};

export const recordVendorInvoice = async (
    id: string,
    body: RecordVendorInvoiceBody
): Promise<PurchaseOrderDetail> => {
    const response = await apiV2.post<{ success: boolean; data: PurchaseOrderDetail }>(
        `${BASE_PATH}/${id}/vendor-invoice`,
        body
    );
    return response.data.data;
};

export interface PurchaseOrdersExportParams {
    // YYYY-MM-DD bounds over the order date; omitted means no restriction
    // (the endpoint has no default window, unlike the invoice export).
    from?: string;
    to?: string;
    chargeId?: string;
    practiceId?: string;
    vendorId?: string[];
    fulfillmentStatus?: string[];
    billingStatus?: string[];
    paymentStatus?: string[];
    needsAttention?: boolean;
    q?: string;
}

export interface PurchaseOrdersExportResult {
    blob: Blob;
    /** The server capped the row count; the range needs narrowing. */
    truncated: boolean;
}

export const exportPurchaseOrdersCsv = async (
    params: PurchaseOrdersExportParams
): Promise<PurchaseOrdersExportResult> => {
    const query: Record<string, string | string[]> = {};
    if (params.from) query.from = params.from;
    if (params.to) query.to = params.to;
    if (params.chargeId) query.chargeId = params.chargeId;
    if (params.practiceId) query.practiceId = params.practiceId;
    if (params.vendorId?.length) query.vendorId = params.vendorId;
    if (params.fulfillmentStatus?.length) query.fulfillmentStatus = params.fulfillmentStatus;
    if (params.billingStatus?.length) query.billingStatus = params.billingStatus;
    if (params.paymentStatus?.length) query.paymentStatus = params.paymentStatus;
    if (params.needsAttention) query.needsAttention = 'true';
    if (params.q) query.q = params.q;

    try {
        const response = await apiV2.get<Blob>(`${BASE_PATH}/export`, {
            params: query,
            responseType: 'blob'
        });
        return {
            blob: response.data,
            truncated: response.headers['x-export-truncated'] === 'true'
        };
    } catch (error) {
        throw await parseBlobExportError(error);
    }
};
